/* Shared inner structure for notification-like components (Alert, PromoBanner):
   the content + action row and the trailing close button, plus the two buttons themselves.
   Alert.Root is the flex container, so this fragment's row and close button land as flex
   siblings next to the leading visual (indicator / illustration).
   The constants here are load-bearing and must not drift across the two components:
   `rem(11)` rowGap + the button `rem(-4)` bleed keep a single-line notification on 48px,
   and `flex: 1 1 rem(240)` on the content lets the action wrap below the text on narrow widths.

   `stackable` (opt-in, currently only PromoBanner) makes the elements this fragment owns
   responsive: below `tablet` the content/action row stacks vertically and the close button
   floats to the top-right corner. It positions the button absolutely, so the consuming
   component must give Alert.Root `position="relative"`. Because the button floats over the
   top-right corner (32px at `inset 8` ⇒ a 40px band), the content row reserves a matching
   inline-end gutter below `tablet` so title/message text never runs under it — independent of
   how tall the leading visual is. The leading visual and the Root's own flex direction / gap /
   padding stay with the consumer — they differ per component. */

import { Alert } from '@chakra-ui/react/alert';
import { Box } from '@chakra-ui/react/box';
import { type ReactNode } from 'react';

import { IconArrowNortheast } from '../../icons';
import { rem } from '../../theme/themeUtils';
import BitkitCloseButton, { type BitkitCloseButtonProps } from '../BitkitCloseButton/BitkitCloseButton';
import BitkitColorButton, { type BitkitColorButtonProps } from '../BitkitColorButton/BitkitColorButton';
import { type BUTTON_COLORS_MAP, type NotificationAction } from './notificationMaps';

type NotificationColorVariant = (typeof BUTTON_COLORS_MAP)[keyof typeof BUTTON_COLORS_MAP];

const ActionButton = ({
  action,
  colorVariant,
}: {
  action: NotificationAction;
  colorVariant: BitkitColorButtonProps['colorVariant'];
}) =>
  action.href !== undefined ? (
    <BitkitColorButton
      colorVariant={colorVariant}
      href={action.href}
      isExternal={action.isExternal || action.target === '_blank'}
      onClick={action.onClick}
      state={action.state}
      suffixIcon={action.isExternal ? IconArrowNortheast : undefined}
      target={action.target}
      marginBlock={rem(-4)}
      marginInlineEnd="12"
      whiteSpace="nowrap"
    >
      {action.label}
    </BitkitColorButton>
  ) : (
    <BitkitColorButton
      colorVariant={colorVariant}
      onClick={action.onClick}
      state={action.state}
      marginBlock={rem(-4)}
      marginInlineEnd="12"
      whiteSpace="nowrap"
    >
      {action.label}
    </BitkitColorButton>
  );

const CloseButton = ({
  colorVariant,
  onClose,
  stackable,
}: {
  colorVariant: BitkitCloseButtonProps['colorVariant'];
  onClose?: () => void;
  stackable?: boolean;
}) => (
  <BitkitCloseButton
    alignSelf="flex-start"
    size="sm"
    onClick={onClose}
    colorVariant={colorVariant}
    // Below tablet (stackable only) it floats to the top-right corner (needs Root
    // position="relative"); otherwise inline with the -4 bleed that keeps a single line on 48px.
    position={stackable ? { base: 'absolute', tablet: 'static' } : undefined}
    insetBlockStart={stackable ? '8' : undefined}
    insetInlineEnd={stackable ? '8' : undefined}
    marginBlock={{ base: stackable ? '0' : rem(-4), tablet: rem(-4) }}
  />
);

export interface NotificationContentProps {
  action?: NotificationAction;
  colorVariant: NotificationColorVariant;
  dismissible?: boolean;
  messageText: ReactNode;
  onClose?: () => void;
  /** Opt in to responsive stacking below `tablet`. Requires Root `position="relative"`. */
  stackable?: boolean;
  titleText?: ReactNode;
}

export const NotificationContent = ({
  action,
  colorVariant,
  dismissible,
  messageText,
  onClose,
  stackable,
  titleText,
}: NotificationContentProps) => (
  <>
    {/* content + action share a wrapping row so the action drops below the text on narrow widths;
        when stacking, the row switches to a column below tablet */}
    <Box
      display="flex"
      flexDirection={stackable ? { base: 'column', tablet: 'row' } : undefined}
      flex={stackable ? { base: '0 1 0%', tablet: '1' } : '1'}
      minWidth="0"
      flexWrap="wrap"
      alignItems={stackable ? { base: 'flex-start', tablet: 'center' } : 'center'}
      columnGap="16"
      rowGap={stackable ? { base: '20', tablet: rem(11) } : rem(11)}
      paddingInlineEnd={stackable && dismissible ? { base: '40', tablet: '0' } : undefined}
    >
      <Alert.Content minWidth="0" flex={stackable ? { base: 'none', tablet: `1 1 ${rem(240)}` } : `1 1 ${rem(240)}`}>
        {titleText && <Alert.Title>{titleText}</Alert.Title>}
        <Alert.Description>{messageText}</Alert.Description>
      </Alert.Content>
      {!!action && <ActionButton action={action} colorVariant={colorVariant} />}
    </Box>
    {!!dismissible && <CloseButton colorVariant={colorVariant} onClose={onClose} stackable={stackable} />}
  </>
);
